import { Stack } from "@fluentui/react";
import { TextField } from "@fluentui/react/lib/TextField";
import { EditIcon } from "@fluentui/react-icons-mdl2";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

import { Account, Contract } from "../../contract.interface";
import { LegalFields, PhysicalFields, ShimmerFields } from "../Fields";
import { validateAccountCompleteness } from "../utils/validateAccountCompleteness";

import { Card } from "@/components/Shared/Card";
import {
  CustomDrawer,
  DrawerActions,
  DrawerStateType
} from "@/components/Shared/CustomDrawer";
import { AccountsService, MaritalStatusService } from "@/core/libs/api";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { IMessageBar, messageBarTypes } from "@/core/libs/message-bar";
import { useDebounce } from "@/hooks/index";
import { useSelectedBu } from "@/hooks/useSelectedBu";
import { CreateAccount } from "@/modules/Accounts/pages/create/CreateAccount";
import { EditAccounts } from "@/modules/Accounts/pages/edit/EditAccount";
import {
  AccountType,
  MaritalStatus,
  MaritalStatusTranslate
} from "@/modules/Accounts/types";
import { cpfCnpjFormatter } from "@/utils/FormatCPForCNPJ";
import { normalizeDocumentNumber } from "@/utils/NormalizeDocumentNumber";
import { validateDocumentNumber } from "@/utils/ValidateDocumentNumber";

interface SearchClientProps {
  contract?: Contract;
  currentBU: number;
  fieldToFocus: string;
  isAccountIncomplete: boolean;
  isEditingAccount: boolean;
  message: IMessageBar;
  setFieldToFocus: (fieldName: string) => void;
  setIsAccountIncomplete: (condition: boolean) => void;
  setIsEditingAccount: (condition: boolean) => void;
  setMessage: ({ message, type, code }: IMessageBar) => void;
  setUpperAccount: (account: Partial<Account>) => void;
}

export function SearchClient({
  currentBU,
  isAccountIncomplete,
  setIsAccountIncomplete,
  setMessage,
  contract,
  setUpperAccount
}: SearchClientProps): JSX.Element {
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(
    contract.accountDocument?.documentNumber
  );
  const [accountIdToEdit, setAccountIdToEdit] = useState(contract?.account?.id);
  const [errorMessage, setErrorMessage] = useState<string>(undefined);
  const [account, setAccount] = useState<Partial<Account>>(contract.account);
  const [documentSearched, setDocumentSearched] = useState<string>();
  const [isSearching, setIsSearching] = useState(false);
  const [initialAccountData, setInitialAccountData] =
    useState<Partial<Account>>(null);
  const [maritalStatusOptions, setMaritalStatusOptions] =
    useState<Array<{ key: number; text: string }>>(null);
  const debouncedSearchTerm: string | undefined = useDebounce(searchTerm, 500);
  const { selectedBU } = useSelectedBu();
  const { execute } = useApi();

  //Drawer
  const drawerContainerRef = useRef<HTMLDivElement>(null);

  const [drawerState, setDrawerState] = useState<DrawerStateType>({
    action: null,
    isOpen: false
  });

  const cleanData = useCallback(() => {
    setMessage(undefined);
    setErrorMessage(undefined);
    setDocumentSearched(undefined);
    setAccount(null);
    setUpperAccount(null);
  }, [setMessage, setUpperAccount]);

  const handleMaritalStatusRequest = async () => {
    const { signal } = new AbortController();
    const service = MaritalStatusService("", true);

    interface MaritalStatusResponse {
      items?: Array<{ id: number; name: MaritalStatus }>;
    }

    const result = await service.list({ params: {}, signal });
    const data = result.data as MaritalStatusResponse;

    setMaritalStatusOptions(
      data?.items?.map(item => ({
        key: item.id,
        text: MaritalStatusTranslate[item.name]
      })) || []
    );
  };

  const searchAccount = useCallback(
    async (documentNumber: string, businessActionId: number) => {
      const response = await execute<Partial<Account>>({
        url: `accounts/search`,
        params: {
          documentNumber,
          businessActionId
        }
      });

      return response.data;
    },

    [execute]
  );

  const getClient = useCallback(async () => {
    setIsAccountIncomplete(false);
    cleanData();

    const documentNumberSanitized =
      normalizeDocumentNumber(debouncedSearchTerm);

    if (validateDocumentNumber(documentNumberSanitized)) {
      setIsSearching(true);
      setDocumentSearched(documentNumberSanitized);

      const account = await searchAccount(documentNumberSanitized, currentBU);

      if (account?.id) {
        setAccountIdToEdit(account.id);
        const validation = validateAccountCompleteness(
          account,
          selectedBU?.paymentMethod
        );

        const shouldDisplayWarningMessage =
          validation.valid && validation.reason;

        if (shouldDisplayWarningMessage) {
          setMessage({
            message: validation.reason,
            type: messageBarTypes.WARNING
          });
        }

        if (!validation.valid) {
          setIsAccountIncomplete(true);
          setErrorMessage(validation.reason);
          openEditDrawer();

          return;
        }

        setIsSearching(false);
        setAccount(account);
        setUpperAccount(account);

        return;
      } else {
        setInitialAccountData(account);
        setAccount(account);
        setIsSearching(false);
        setUpperAccount(account);
        setIsCreatingAccount(true);
        openCreateDrawer();
      }
    } else {
      setAccount(null);
      setUpperAccount(null);
      setErrorMessage("Número de documento inválido");
      setMessage(undefined);
    }
  }, [
    currentBU,
    debouncedSearchTerm,
    selectedBU?.paymentMethod,
    setIsAccountIncomplete,
    setMessage,
    setUpperAccount,
    cleanData,
    searchAccount
  ]);

  useEffect(() => {
    if (
      debouncedSearchTerm &&
      contract?.accountDocument?.documentNumber !== debouncedSearchTerm
    ) {
      cleanData();
      void getClient();
    }
  }, [
    cleanData,
    contract?.accountDocument?.documentNumber,
    debouncedSearchTerm,
    getClient
  ]);

  function closeDrawer() {
    setDrawerState({ action: null, isOpen: false });
  }

  function openCreateDrawer() {
    setDrawerState({ action: DrawerActions.CREATE, isOpen: true });
  }

  function openEditDrawer() {
    setDrawerState({ action: DrawerActions.EDIT, isOpen: true });
  }

  async function createAccounts(values, _, closeDrawer) {
    const { signal } = new AbortController();
    const service = AccountsService("");
    const result = await service.create({
      params: { body: values, query: { businessActionId: selectedBU?.id } },
      signal
    });

    if (result?.response?.ok) {
      const documentNumber = values.documents.find(
        value => value.documentType === "CPF" || value.documentType === "CNPJ"
      ).documentNumber;

      setSearchTerm(documentNumber);
      setIsCreatingAccount(false);
    } else {
      setErrorMessage(result.error.message);

      return result;
    }
    closeDrawer();

    return result;
  }

  async function editAccount(values, _, closeDrawer) {
    const { signal } = new AbortController();
    const service = AccountsService("");
    const result = await service.edit({
      params: {
        ...values,
        query: { businessActionId: selectedBU?.id },
        id: accountIdToEdit
      },
      signal
    });

    if (result?.response?.ok) {
      closeDrawer();
    }

    return result;
  }

  useEffect(() => {
    void handleMaritalStatusRequest();
  }, []);

  return (
    <Stack styles={{ root: { width: "100%" } }}>
      <Helmet>
        <title>Buscar conta do cooperado - Libertas</title>
      </Helmet>
      <Stack tokens={{ childrenGap: 15 }}>
        <Card>
          <TextField
            id="input-search-client"
            disabled={Boolean(contract?.deletedAt)}
            placeholder="Digite o CPF/CNPJ do cooperado."
            onKeyDown={event => {
              if (event.key === "Enter") {
                void getClient();
              }
            }}
            onChange={(_event, item) => {
              setSearchTerm(cpfCnpjFormatter(item));
            }}
            maxLength={18}
            value={cpfCnpjFormatter(searchTerm || "")}
            errorMessage={errorMessage}
            onRenderLabel={() => (
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
                style={{ padding: 10 }}
              >
                {account && !contract?.deletedAt && (
                  <EditIcon
                    style={{
                      cursor: "pointer",
                      fontSize: 16,
                      color: "#395ca3"
                    }}
                    title="Editar registro de cooperado"
                    onClick={openEditDrawer}
                  />
                )}
                <span style={{ fontWeight: 600 }}>CPF/CNPJ</span>
              </Stack>
            )}
          />
          {isSearching && <ShimmerFields />}

          {!isAccountIncomplete &&
            account?.accountType === AccountType.PHYSICAL && (
              <PhysicalFields account={account} />
            )}

          {!isAccountIncomplete &&
            account?.accountType === AccountType.LEGAL && (
              <LegalFields account={account} />
            )}

          <CustomDrawer
            isOpen={drawerState.isOpen}
            action={drawerState.action}
            errorMessage={errorMessage}
            module={"Contas dos Cooperados"}
            size="medium"
            onCloseDrawer={closeDrawer}
            drawerContainerRef={drawerContainerRef}
          >
            {isCreatingAccount && (
              <CreateAccount
                create={createAccounts}
                message={null}
                drawerContainerRef={drawerContainerRef}
                dismissMessage={() => {
                  return;
                }}
                closeDrawer={closeDrawer}
                document={documentSearched}
                maritalStatusOptions={maritalStatusOptions}
                initialAccountData={initialAccountData}
              />
            )}
            {accountIdToEdit && (
              <EditAccounts
                edit={editAccount}
                selectedItem={accountIdToEdit}
                message={null}
                drawerContainerRef={drawerContainerRef}
                closeDrawer={closeDrawer}
                dismissMessage={() => {
                  return;
                }}
                maritalStatusOptions={maritalStatusOptions}
                onSubmitSuccess={void getClient}
              />
            )}
          </CustomDrawer>
        </Card>
      </Stack>
    </Stack>
  );
}
